import {
  Alert,
  Button,
  Card,
  Empty,
  Input,
  Modal,
  Pagination,
  Popconfirm,
  Space,
  Spin,
  Table,
  Typography,
} from "antd"
import { useState } from "react"
import { Link } from "react-router-dom"
import { useRegulatorySession } from "../../providers/regulatoryProvider"
import type { PhysicalLink, PhysicalLinkEvent } from "../../types/physical"
import { physicalApi, equipmentLinkWrite, physicalError } from "../../utils/physicalApi"
import { regulatoryApi, RegulatoryApiError } from "../../utils/regulatoryApi"
import { IdentityPicker } from "./IdentityPicker"
import { usePhysicalResource } from "./usePhysicalResource"
import "../../pages/PhysicalSites/physical.css"
export function PhysicalLinkPanel(props: {
  id: string
  equipment?: boolean
  refreshKey?: string | number
  disabled?: boolean
  onChanged?: () => void
}) {
  const { sessionKey } = useRegulatorySession()
  return props.id ? <LinkPanel key={`${sessionKey}/${props.id}`} {...props} /> : null
}
function LinkPanel({
  id,
  equipment = false,
  refreshKey = "",
  disabled,
  onChanged,
}: {
  id: string
  equipment?: boolean
  refreshKey?: string | number
  disabled?: boolean
  onChanged?: () => void
}) {
  const [editing, setEditing] = useState(false),
    [history, setHistory] = useState(false)
  const resource = usePhysicalResource(`link/${id}/${equipment}/${refreshKey}`, (signal) =>
    physicalApi.link(id, equipment, signal),
  )
  const target = equipment ? resource.data?.physicalAssetUuid : resource.data?.physicalSiteUuid
  const identity = usePhysicalResource(target ? `link-name/${equipment}/${target}` : "", (signal) =>
    physicalApi.get(equipment ? "asset" : "site", target!, signal),
  )
  if (resource.error instanceof RegulatoryApiError && resource.error.status === 404)
    return (
      <Alert
        type="info"
        message="Le dossier sera disponible après la synchronisation de cette inspection avec le cloud."
      />
    )
  return (
    <Card
      size="small"
      className="physical-link-panel"
      title={equipment ? "Équipement physique permanent" : "Site"}
      extra={
        <Space wrap>
          <Button
            size="small"
            disabled={!resource.data || disabled}
            onClick={() => setEditing(true)}
          >
            Rattacher / modifier
          </Button>
          <Button size="small" disabled={!resource.data} onClick={() => setHistory(true)}>
            Historique des liens
          </Button>
        </Space>
      }
    >
      {resource.loading ? (
        <Spin size="small" />
      ) : resource.error ? (
        <Alert
          type="error"
          message={physicalError(resource.error)}
          action={<Button onClick={resource.reload}>Réessayer</Button>}
        />
      ) : (
        <Space direction="vertical" size={3}>
          {target ? (
            <Link to={`/${equipment ? "physical-assets" : "physical-sites"}/${target}`}>
              {identity.data?.name ?? "Ouvrir le dossier"}
            </Link>
          ) : (
            <Typography.Text type="secondary">
              {equipment
                ? "Aucun équipement physique rattaché."
                : "Cette inspection n’est pas encore rattachée à un site."}
            </Typography.Text>
          )}
          {equipment && resource.data?.physicalSiteUuid && (
            <Link to={`/physical-sites/${resource.data.physicalSiteUuid}`}>
              Dossier du site
            </Link>
          )}
          {disabled && (
            <Typography.Text type="secondary">
              Enregistrez ou abandonnez la configuration des composants avant de modifier ces liens.
            </Typography.Text>
          )}
        </Space>
      )}
      {editing && (
        <LinkEditor
          id={id}
          equipment={equipment}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false)
            resource.reload()
            identity.reload()
            onChanged?.()
          }}
        />
      )}
      {history && <LinkHistory id={id} equipment={equipment} onClose={() => setHistory(false)} />}
    </Card>
  )
}
function LinkEditor({
  id,
  equipment,
  onCancel,
  onSaved,
}: {
  id: string
  equipment: boolean
  onCancel: () => void
  onSaved: () => void
}) {
  const resource = usePhysicalResource(`edit-link/${id}/${equipment}`, async (signal) => {
    const link = await physicalApi.link(id, equipment, signal)
    const config = equipment ? await regulatoryApi.equipmentConfiguration(id, signal) : null
    return { link, config }
  })
  const [draft, setDraft] = useState<{
    base: PhysicalLink
    target: string | null
    components: Record<string, string | null>
  }>()
  const current =
    draft ??
    (resource.data
      ? {
          base: resource.data.link,
          target: equipment
            ? resource.data.link.physicalAssetUuid ?? null
            : resource.data.link.physicalSiteUuid,
          components: Object.fromEntries(
            resource.data.link.components.map((c) => [c.contextUuid, c.physicalComponentUuid]),
          ),
        }
      : undefined)
  const [reason, setReason] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()
  const [conflict, setConflict] = useState(false)
  const save = async () => {
    if (!current || !reason.trim()) return
    setBusy(true)
    setError(undefined)
    try {
      await physicalApi.saveLink(
        id,
        equipment,
        equipment
          ? equipmentLinkWrite(current.base, current.target, current.components, reason)
          : {
              expectedVersion: current.base.version,
              physicalSiteUuid: current.target,
              reason: reason.trim(),
            },
      )
      onSaved()
    } catch (e) {
      setError(physicalError(e))
      setConflict((e as { status?: number }).status === 409)
    } finally {
      setBusy(false)
    }
  }
  const labels = new Map(resource.data?.config?.contexts.map((c) => [c.uuid, c.label]) ?? [])
  return (
    <Modal
      open
      title={
        equipment
          ? "Rattacher l’équipement et ses composants"
          : "Rattacher cette inspection à un site"
      }
      onCancel={onCancel}
      onOk={save}
      confirmLoading={busy}
      maskClosable={false}
      closable={!busy}
      cancelText="Annuler"
      cancelButtonProps={{ disabled: busy }}
      okText="Enregistrer les liens"
      okButtonProps={{
        disabled: !current || !reason.trim() || resource.loading || reason.length > 2000,
      }}
    >
      <Alert
        type="info"
        message="Ce rattachement conserve les réponses, les photos et les informations de cette inspection."
        style={{ marginBottom: 12 }}
      />
      {(error || resource.error) && (
        <Alert
          type="error"
          message={error ?? physicalError(resource.error)}
          action={
            conflict ? (
              <Popconfirm
                title="Remplacer votre choix par les liens actuels du serveur ?"
                onConfirm={() => {
                  setDraft(undefined)
                  setError(undefined)
                  setConflict(false)
                  resource.reload()
                }}
              >
                <Button disabled={busy}>Recharger les liens</Button>
              </Popconfirm>
            ) : resource.error ? (
              <Button onClick={resource.reload}>Réessayer</Button>
            ) : undefined
          }
          style={{ marginBottom: 12 }}
        />
      )}
      {resource.loading && <Spin />}
      {current && (
        <div className="physical-link-fields">
          {equipment && !current.base.physicalSiteUuid && (
            <Alert
              type="warning"
              message="Rattachez d’abord l’inspection à un site."
              description={
                <Link to={`/sites/${current.base.inspectionUuid}`}>Ouvrir l’inspection</Link>
              }
            />
          )}
          <label className="physical-link-field">
            <span>{equipment ? "Équipement de l’inventaire" : "Site"}</span>
            <IdentityPicker
              kind={equipment ? "asset" : "site"}
              parent={equipment ? current.base.physicalSiteUuid : undefined}
              value={current.target}
              disabled={busy}
              label={equipment ? "Choisir l’équipement physique" : "Choisir le site"}
              onChange={(target) =>
                setDraft({
                  ...current,
                  target,
                  components: equipment && target !== current.target ? {} : current.components,
                })
              }
            />
          </label>
          {equipment &&
            current.base.components.map((c) => (
              <label className="physical-link-field" key={c.contextUuid}>
                <span>{labels.get(c.contextUuid) ?? "Composant de l’inspection"}</span>
                <IdentityPicker
                  kind="component"
                  parent={current.target}
                  value={current.components[c.contextUuid]}
                  disabled={busy || !current.target}
                  label={`Composant physique : ${labels.get(c.contextUuid) ?? c.contextUuid}`}
                  onChange={(v) =>
                    setDraft({
                      ...current,
                      components: { ...current.components, [c.contextUuid]: v },
                    })
                  }
                />
              </label>
            ))}
          <label className="physical-link-field">
            <span>Motif du rattachement ou de sa correction *</span>
            <Input.TextArea
              aria-label="Motif du changement de liens"
              rows={3}
              maxLength={2000}
              showCount
              value={reason}
              disabled={busy}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <Typography.Text type="secondary">
            Effacez la sélection pour dissocier. L’historique des liens est conservé.
          </Typography.Text>
        </div>
      )}
    </Modal>
  )
}
function LinkHistory({
  id,
  equipment,
  onClose,
}: {
  id: string
  equipment: boolean
  onClose: () => void
}) {
  const [page, setPage] = useState(1)
  const resource = usePhysicalResource(`audit/${id}/${equipment}/${page}`, (signal) =>
    physicalApi.history(id, equipment, { limit: 10, offset: (page - 1) * 10 }, signal),
  )
  return (
    <Modal
      open
      width={850}
      title="Historique des rattachements"
      onCancel={onClose}
      footer={<Button onClick={onClose}>Fermer</Button>}
    >
      {resource.error && (
        <Alert
          type="error"
          message={physicalError(resource.error)}
          action={<Button onClick={resource.reload}>Réessayer</Button>}
        />
      )}
      <Table<PhysicalLinkEvent>
        dataSource={resource.data?.items ?? []}
        loading={resource.loading}
        rowKey="uuid"
        pagination={false}
        scroll={{ x: 620 }}
        locale={{ emptyText: <Empty description="Aucun changement de liens" /> }}
        columns={[
          {
            title: "Date",
            dataIndex: "createdAt",
            render: (v) => new Date(v).toLocaleString("fr-FR"),
          },
          { title: "Motif", dataIndex: "reason" },
          {
            title: "Cible",
            key: "target",
            render: (_, v) =>
              v.equipmentUuid ? (
                <Link to={`/equipments/${v.equipmentUuid}`}>Équipement inspecté</Link>
              ) : (
                "Inspection"
              ),
          },
        ]}
        expandable={{
          expandedRowRender: (v) => (
            <Space direction="vertical">
              <Typography.Text type="secondary">Auteur : {v.actorUuid}</Typography.Text>
              <LinkSnapshot title="Avant" value={v.previousLinks} />
              <LinkSnapshot title="Après" value={v.newLinks} />
            </Space>
          ),
        }}
      />
      <Pagination
        current={page}
        pageSize={10}
        total={resource.data?.total ?? 0}
        showSizeChanger={false}
        onChange={setPage}
      />
    </Modal>
  )
}
function LinkSnapshot({ title, value }: { title: string; value: PhysicalLink }) {
  return (
    <Space wrap>
      <strong>{title}</strong>
      {value.physicalSiteUuid ? (
        <Link to={`/physical-sites/${value.physicalSiteUuid}`}>Site</Link>
      ) : (
        "Sans site"
      )}
      {value.physicalAssetUuid && (
        <Link to={`/physical-assets/${value.physicalAssetUuid}`}>Équipement physique</Link>
      )}
      {value.components
        .filter((c) => c.physicalComponentUuid)
        .map((c) => (
          <Link key={c.contextUuid} to={`/physical-components/${c.physicalComponentUuid}`}>
            Composant {c.physicalComponentUuid?.slice(0, 8)}
          </Link>
        ))}
    </Space>
  )
}
